import { format } from 'date-fns';
import { useMyPastDueMembers } from '@/hooks/members/useMyPastDueMembers';
import type { MemberDataset } from '@/lib/member-views/memberDataset.types';
import { sortFnTags } from '@/lib/member-views/sortRows';
import type { PastDueMemberRow } from '@/lib/members/members.types';
import { reportOnly as reportOnlyFor } from './reportOnly';

const reportOnly = reportOnlyFor<PastDueMemberRow>;

/**
 * Past Due — the practice's Active members 1–60 days past due on their membership payment,
 * company-paid corporate members excluded, for following up before they're suspended.
 *
 * The report is the 41 columns below, in `csv.order`: legacy's report with Last AWP Date added and
 * its two aging columns replaced by all five buckets. Days Past Due and Last AWP are the table's own
 * fields; the table's Patient and Phone combine two values each, so the report has its own name and
 * phone columns.
 */
export const pastDueMembersDataset: MemberDataset<PastDueMemberRow> = {
  key: 'past-due',
  rowKey: (row) => row.id,
  useRows: useMyPastDueMembers,
  alphaKey: (row) => row.lastName,
  // Most overdue first is what the view is for. The server already returns that order; sorting on
  // it marks the header.
  defaultSort: [{ id: 'daysPastDue', desc: true }],
  // The same provisional ceiling as All Active; these members are a subset of it.
  loading: { mode: 'full', ceiling: 10_000 },
  // Local date, not `toISOString()`, which is already tomorrow on a US evening.
  csvFileName: ({ date }) => `My_Patient_Past_Due_${format(date, 'yyyy-MM-dd')}.csv`,
  fields: [
    {
      id: 'patient',
      label: 'memberViews.columns.patient',
      width: 'xl',
      // Sorts on "last first"; the cell shows the name the other way round, with the MDVIP ID.
      value: (row) => [row.lastName, row.firstName].filter(Boolean).join(' ') || null,
      table: true,
      sortable: true,
    },
    {
      id: 'daysPastDue',
      label: 'memberViews.columns.daysPastDue',
      width: 'md',
      value: (row) => row.billingDaysPastDue,
      format: 'number',
      table: true,
      sortable: true,
      csv: { order: 36, label: 'memberViews.csv.columns.daysPastDue' },
    },
    {
      id: 'phone',
      label: 'memberViews.columns.phone',
      width: 'lg',
      value: (row) => row.mobilePhone ?? row.phone,
      format: 'phone',
      table: true,
    },
    {
      id: 'priorityTags',
      label: 'memberViews.columns.priorityTags',
      width: 'xl',
      // No tags is a missing value, so untagged rows sort last both ways, as the design asks.
      value: (row) => (row.priorityTags.length > 0 ? row.priorityTags : null),
      table: true,
      sortable: true,
      sortFn: sortFnTags,
    },
    {
      id: 'lastAwp',
      label: 'memberViews.columns.lastAwp',
      width: 'sm',
      value: (row) => row.lastAwpDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 14, label: 'memberViews.csv.columns.lastAwpDate' },
    },
    reportOnly('title', 1, 'memberViews.csv.columns.title', (row) => row.salutation),
    reportOnly('lastName', 2, 'memberViews.csv.columns.lastName', (row) => row.lastName),
    reportOnly('firstName', 3, 'memberViews.csv.columns.firstName', (row) => row.firstName),
    reportOnly('mdvipId', 4, 'memberViews.csv.columns.mdvipId', (row) => row.mdvipId),
    reportOnly('established', 5, 'memberViews.csv.columns.dateEstablished', (row) => row.memberSince, 'date'),
    reportOnly('renewalDate', 6, 'memberViews.csv.columns.renewalDate', (row) => row.renewalDate, 'date'),
    reportOnly('mailingStreet', 7, 'memberViews.csv.columns.mailingStreet', (row) => row.mailingStreet),
    reportOnly('city', 8, 'memberViews.csv.columns.city', (row) => row.mailingCity),
    reportOnly('state', 9, 'memberViews.csv.columns.state', (row) => row.mailingState),
    reportOnly('zip', 10, 'memberViews.csv.columns.zip', (row) => row.mailingZip),
    // The phone numbers as the API returns them, as legacy's report printed them.
    reportOnly('homePhone', 11, 'memberViews.csv.columns.phone', (row) => row.phone),
    reportOnly('cellPhone', 12, 'memberViews.csv.columns.cellPhone', (row) => row.mobilePhone),
    reportOnly('email', 13, 'memberViews.csv.columns.email', (row) => row.email),
    reportOnly('dob', 15, 'memberViews.csv.columns.dob', (row) => row.dateOfBirth, 'date'),
    // The current agreement's fee, not the membership's list price.
    reportOnly(
      'membershipFee',
      16,
      'memberViews.csv.columns.membershipFee',
      (row) => row.currentAgreementMsrp,
      'currency',
    ),
    reportOnly('autoPay', 17, 'memberViews.csv.columns.autoPay', (row) => row.autoPay, 'yesBlank'),
    reportOnly('lastPaymentDate', 18, 'memberViews.csv.columns.lastPaymentDate', (row) => row.lastPaymentDate, 'date'),
    reportOnly(
      'lastPaymentAmount',
      19,
      'memberViews.csv.columns.lastPaymentAmount',
      (row) => row.lastPaymentAmount,
      'currency',
    ),
    reportOnly('amountDue', 20, 'memberViews.csv.columns.amountDue', (row) => row.currentBalanceFormula, 'currency'),
    reportOnly('cancelReason', 21, 'memberViews.csv.columns.cancelReason', (row) => row.lastCancelReason),
    // Printed as the API returns them, as legacy did; only the dunning steps below were dates there.
    reportOnly('ccDeclineCall', 22, 'memberViews.csv.columns.ccDeclineCall', (row) => row.ccDeclineCall),
    reportOnly('ccDeclineLetter', 23, 'memberViews.csv.columns.ccDeclineLetter', (row) => row.ccDeclineLetter),
    reportOnly('call1', 24, 'memberViews.csv.columns.call1', (row) => row.dunCall1, 'date'),
    reportOnly('letter1', 25, 'memberViews.csv.columns.letter1', (row) => row.dunLetter1, 'date'),
    reportOnly('email1', 26, 'memberViews.csv.columns.email1', (row) => row.dunEmail1, 'date'),
    reportOnly('call2', 27, 'memberViews.csv.columns.call2', (row) => row.dunCall2, 'date'),
    reportOnly('letter2', 28, 'memberViews.csv.columns.letter2', (row) => row.dunLetter2, 'date'),
    reportOnly('email2', 29, 'memberViews.csv.columns.email2', (row) => row.dunEmail2, 'date'),
    reportOnly('call3', 30, 'memberViews.csv.columns.call3', (row) => row.dunCall3, 'date'),
    reportOnly('letter3', 31, 'memberViews.csv.columns.letter3', (row) => row.dunLetter3, 'date'),
    reportOnly('email3', 32, 'memberViews.csv.columns.email3', (row) => row.dunEmail3, 'date'),
    reportOnly('call4', 33, 'memberViews.csv.columns.call4', (row) => row.dunCall4, 'date'),
    reportOnly('letter4', 34, 'memberViews.csv.columns.letter4', (row) => row.dunLetter4, 'date'),
    reportOnly('email4', 35, 'memberViews.csv.columns.email4', (row) => row.dunEmail4, 'date'),
    // Headed one bucket later than the API's field names (`aging30` is "31-60 Days"), as the
    // Suspended report. The last three are almost always $0: the view stops at 60 days.
    reportOnly('aging0to30', 37, 'memberViews.csv.columns.aging0to30', (row) => row.agingCurrent, 'currency'),
    reportOnly('aging31to60', 38, 'memberViews.csv.columns.aging31to60', (row) => row.aging30, 'currency'),
    reportOnly('aging61to90', 39, 'memberViews.csv.columns.aging61to90', (row) => row.aging60, 'currency'),
    reportOnly('aging91to120', 40, 'memberViews.csv.columns.aging91to120', (row) => row.aging90, 'currency'),
    reportOnly('aging120Plus', 41, 'memberViews.csv.columns.aging120Plus', (row) => row.aging120, 'currency'),
  ],
};
